'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { SearchSelectBlock } from '@govinity/ui/components/form-blocks/search-select-block/index';
import type { SelectOption } from '@govinity/ui/components/form-blocks/select-block';
import { ButtonLoadingSpinner } from '@govinity/ui/components/ui/button-loading-spinner';
import { Form } from '@govinity/ui/components/ui/form';
import { useToast } from '@govinity/ui/hooks/use-toast';
import type { PublicSpeakerSignupNode } from '@govinity/validation/public';
import { useMutation } from '@tanstack/react-query';
import { Mic } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useAuthStore } from '@/stores/authStore';
import api from '@/lib/api';
import { toEntryKey } from './helpers';
import { useGovernmentContext } from '../../../context';
import { useMeetingContext } from '../meeting-context';

const formSchema = z.object({
  entry: z.string().min(1, 'Please choose what you would like to speak on'),
});

type SignupFormValues = z.infer<typeof formSchema>;

type SignupFormProps = {
  onSuccess: () => void;
  sections: PublicSpeakerSignupNode[];
  signedUpKeys: Set<string>;
};

const toSignupBody = (value: string, name: string) => {
  const [type, id] = value.split(':');
  const numericId = Number(id);

  return type === 'section'
    ? { agendaSectionId: numericId, name }
    : { agendaSectionLegislativeItemId: numericId, name };
};

const toSelectOptions = (
  nodes: PublicSpeakerSignupNode[],
  signedUpKeys: Set<string>,
  signedUpSuffix: string,
  depth = 0
): SelectOption[] =>
  nodes.flatMap((node) => {
    const isSignedUp = signedUpKeys.has(toEntryKey(node));

    return [
      {
        value: toEntryKey(node),
        // Non-breaking spaces keep the nesting visible (plain spaces collapse).
        label: `${'\u00a0\u00a0'.repeat(depth)}${node.name}${isSignedUp ? ` ${signedUpSuffix}` : ''}`,
        disabled: node.disabled || isSignedUp,
      },
      ...toSelectOptions(node.children, signedUpKeys, signedUpSuffix, depth + 1),
    ];
  });

export const SignupForm = ({ onSuccess, sections, signedUpKeys }: SignupFormProps) => {
  const { government } = useGovernmentContext();
  const { meeting } = useMeetingContext();
  const user = useAuthStore((state) => state.user);
  const t = useTranslations('PortalPublicSpeakerSignup');
  const { toast } = useToast();

  const speakerName = useMemo(() => {
    return [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '';
  }, [user]);

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { entry: '' },
  });

  const { mutate: signUp, isPending } = useMutation({
    mutationFn: ({ entry }: SignupFormValues) =>
      api.governments
        .publicSpeakers(government.code)
        .signUpAsUser(meeting.id, toSignupBody(entry, speakerName)),
    onSuccess: () => {
      form.reset({ entry: '' });
      onSuccess();
    },
    onError: (error) => {
      toast({
        title: t('signUpErrorTitle'),
        description: error instanceof Error ? error.message : t('signUpErrorDescription'),
        variant: 'destructive',
      });
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((values) => signUp(values))} className="space-y-4">
        <SearchSelectBlock
          form={form}
          name="entry"
          label={t('entryLabel')}
          options={toSelectOptions(sections, signedUpKeys, t('alreadySignedUpOption'))}
          inputProps={{
            isDisabled: isPending,
            isOptionDisabled: (option) => !!option.disabled,
          }}
        />

        <p className="text-muted-foreground text-sm">
          {t('speakerNameNote', { name: speakerName })}
        </p>

        <ButtonLoadingSpinner
          aria-label={t('submitButton')}
          className="w-full"
          disabled={isPending}
          icon={<Mic aria-hidden="true" />}
          isLoading={isPending}
          text={t('submitButton')}
          type="submit"
        />
      </form>
    </Form>
  );
};
